/**
 * aiDisclosure — the AI disclosure text, in ONE place, for both surfaces that
 * show it.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ★★★ THE DEFECT THIS CLOSED (v242.153.0). The patient consents at onboarding
 * against the full disclosure and then Settings → AI & Data, the ONLY place they
 * can re-read what they agreed to, showed a one-line intro and two four-word
 * bullets. The screen whose entire job is to answer "what did I agree to?"
 * answered with less than the agreement.
 *
 * ★★ TWO COPIES OF ONE DISCLOSURE IS THE FAMILY OF BUG THIS CODEBASE KEEPS PAYING
 *   FOR. Duplicating the paragraphs into the settings screen would mean a future
 *   wording change lands on one surface, the patient reads two different promises,
 *   and the consent ledger's `version` is pinned to whichever one the author
 *   happened to open. So it is one array, read by both.
 *
 * ═══ v242.155.0 · THE COPY PASS WITH DR. RAMI ══════════════════════════════
 * ★★★ THE OLD DOCTOR LINE WAS FALSE AND HAD TO GO. It said *"A real doctor
 *   reviews anything the AI suggests before it reaches you."* `ai_service.py`
 *   generates `differentialEducation` — up to five possible conditions in plain
 *   language — and the PATIENT reads it before any physician opens the case.
 *   ★★ AND THE REPLACEMENT IS NOT A WEAKER VERSION OF THE SAME PROMISE, IT IS A
 *     DIFFERENT AND TRUE ONE. Dr. Rami: *"I don't want to pass the impression that
 *     a doctor is literally reviewing every output from Orella. We have gated
 *     Orella chat so it has leeway within a subset of the space, that expands as
 *     the MD approves and diagnoses and orders a treatment plan."* That is
 *     measurable, not aspirational: `agent_chat` resolves a STAGE, and only a case
 *     with a finalized initial encounter reaches `post_visit` and is given
 *     `diagnosis` / `avs_content` / `care_plan` as context. Before the physician
 *     finalizes, Orella has no diagnosis to discuss because it is handed none.
 *     ★★★ SO THE DOCTOR'S AUTHORITY IS A BOUNDARY, NOT AN INSPECTION — and the
 *       copy says exactly that, plus the async property as the BENEFIT it is
 *       ("so you can ask questions any time"), because an unstated gap reads as a
 *       gap. `safeLimit` still closes the block, so a widening SCOPE is never
 *       mistaken for widening AUTHORITY.
 *
 * ★★ "scrambled" → "encrypted", AND THE SPANISH WAS RIGHT FIRST. es said `Se
 *   cifra` — the precise word — for four months while en said "scrambled". A
 *   translation more accurate than its source is a signal the source was never
 *   read closely.
 *
 * ★★★ `learn` IS A PERMISSION, WHICH IS WHY IT IS ITS OWN BLOCK AND WHY IT SAYS
 *   "MAY". It sat inside "How we keep it safe" as a flat *never*; it is now a
 *   scoped never plus a stated intent. Burying a permission in a list of
 *   reassurances is how a disclosure stops being one. Nothing is built behind it
 *   yet — `grep -rn "deidentif\|de-identif" orella-api/api` is empty — so the copy
 *   asks for the door to be open and does not claim to be walking through it.
 *   ★★ CONSENT VERSIONING (PROD-1225). `AI_DISCLOSURE_VERSION` below is the
 *     version of the text this build shows, and `aiConsentAPI.record()` sends it
 *     (`{ version }`) so the ledger row records what the patient saw. The SERVER's
 *     current version S is admin-managed (`app_config.legal_content.ai_disclosure
 *     .version`, then the env var, then 'v1'). POST /ai-consent records the sent
 *     version when it is S or S+1, answers 409 `ai_consent_update_required` (the
 *     "please update the Orella app" sentence, nothing written) when it is older
 *     than S, and 400 when it is malformed or further ahead. A stored version
 *     >= S counts as current. The enforcement switch `AI_CONSENT_ENFORCE` is dormant
 *     until armed.
 *     ★ THE v2 ROLLOUT ORDER, and no other:
 *       (1) ship the branch that sends the constant and shows the 409 sentence
 *           (web AND an iOS build: iOS bundles `dist`, so a native build keeps
 *           whatever it shipped with);
 *       (2) ship the v2 copy with this constant at 'v2', on web and iOS (S+1 is
 *           accepted, so it records v2 honestly before the bump);
 *       (3) only then bump the admin version to v2 (older builds then get the 409
 *           sentence on consent and the 403 on AI calls; v2 records stay current);
 *       (4) the switch `AI_CONSENT_ENFORCE` is set by Dr. Rami.
 *     Bumping the constant before (1) is everywhere, or the admin version before
 *     (2) is, breaks that order.
 * ═══════════════════════════════════════════════════════════════════════════
 */

/**
 * The version of the disclosure text this build shows. `aiConsentAPI.record()` sends it; the
 * version the SERVER compares against is admin-managed, not this constant — see the header
 * for the v2 rollout order.
 */
export const AI_DISCLOSURE_VERSION = 'v1'


/**
 * The disclosure, resolved against the caller's `t`.
 * Each block is `{ id, icon, title, lines: [{ text, strong, onboardingOnly }] }`.
 * A surface renders the lines as one paragraph — the WORDS are the contract, the
 * layout is not.
 *
 * ★ `onboardingOnly` exists for exactly one line. "You can change your mind at any
 *   time in Settings → AI & Data" is reassurance at the moment of agreeing, and
 *   pure redundancy on the Settings screen — where it tells the patient where they
 *   already are, directly above a Withdraw consent button. One shared array, two
 *   surfaces, one line that is only true-to-purpose on one of them.
 */
export function aiDisclosureBlocks(t, { surface = 'onboarding' } = {}) {
  const T = (k, d) => t(`onboarding.aiConsent.${k}`, { defaultValue: d })
  const blocks = [
    {
      id: 'what',
      icon: 'send',
      title: T('whatTitle', 'What we send'),
      lines: [{
        text: T('whatBody',
          'Health information such as your symptoms and case details, the messages you send us, demographics (such as age and sex), plus any documents or photos you upload.'),
      }],
    },
    {
      id: 'who',
      icon: 'cloud',
      title: T('whoTitle', 'Who processes it'),
      lines: [{
        text: T('whoBody',
          "Anthropic's Claude, operated by Amazon Web Services (AWS Bedrock) inside an encrypted environment."),
      }],
    },
    {
      id: 'safe',
      icon: 'shield',
      title: T('safeTitle', 'How we keep it safe'),
      lines: [
        { text: T('safeEnc', "It's encrypted everywhere it travels and everywhere it's stored.") },
        { text: T('safeGate',
          "Your doctor sets what Orella can discuss with you, and that widens once they've reviewed your case and decided your plan — so you can ask questions any time.") },
        // ★ the hard limit. It closes the block ON PURPOSE: read in order, the
        //   patient learns the scope grows and then, immediately, what never does.
        { text: T('safeLimit', 'Only a doctor can diagnose you or decide your treatment.'), strong: true },
        { text: T('safeWithdraw', 'You can change your mind at any time in Settings → AI & Data.'),
          onboardingOnly: true },
      ],
    },
    {
      id: 'learn',
      icon: 'trending',
      title: T('learnTitle', 'How Orella learns'),
      lines: [
        { text: T('learnNever', 'Your identifiable health information is never used to train AI.'), strong: true },
        { text: T('learnAnon',
          'We may use fully anonymized clinical data — no name, no date of birth, nothing that points back to you — to help Orella get better at recognizing conditions.') },
      ],
    },
  ]
  if (surface === 'onboarding') return blocks
  return blocks.map((b) => ({ ...b, lines: b.lines.filter((l) => !l.onboardingOnly) }))
}
